/**
 * Lieferungen Seite - Erweitert mit Dispatch Panel
 * 
 * Features:
 * - Übersicht aller Lieferungen
 * - Fahrer-Dispatch Panel (neues Tab)
 * - Fahrer Check-in/Check-out
 * - Statistiken & Ranking
 */
import React, { useState, useEffect, useCallback } from 'react';
import { deliveryApi } from '../utils/deliveryApi';
import { formatCurrency, formatDate } from '../utils/helpers';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ScrollArea } from "@/components/ui/scroll-area";
import { toast } from 'sonner';
import {
  Package, Truck, Users, RefreshCw, MapPin, Phone, Clock,
  CheckCircle, UserCheck, UserX, Navigation, Euro, TrendingUp,
  Trophy, Star, ArrowRight, Bike, Car, AlertCircle
} from 'lucide-react';

const API_BASE = process.env.REACT_APP_BACKEND_URL || '';

const STATUS_CONFIG = {
  new: { label: 'Neu', color: 'bg-blue-100 text-blue-800 border border-blue-300', icon: '🆕', priority: 1 },
  preparing: { label: 'In Zubereitung', color: 'bg-yellow-100 text-yellow-800 border border-yellow-300', icon: '👨‍🍳', priority: 2 },
  ready: { label: 'Bereit', color: 'bg-green-100 text-green-800 border-2 border-green-500 font-bold shadow-md', icon: '✅', priority: 3 },
  dispatched: { label: 'Unterwegs', color: 'bg-purple-100 text-purple-800 border border-purple-300', icon: '🚗', priority: 4 },
  delivered: { label: 'Zugestellt', color: 'bg-gray-100 text-gray-600 border border-gray-300', icon: '✓', priority: 99 },
  cancelled: { label: 'Abgebrochen', color: 'bg-red-100 text-red-800 border border-red-300', icon: '✕', priority: 99 },
};

function Lieferungen() {
  const [activeTab, setActiveTab] = useState('orders');
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [message, setMessage] = useState(null);
  
  // Fahrer-Zuweisung State
  const [drivers, setDrivers] = useState([]);
  const [showAssignDialog, setShowAssignDialog] = useState(false);
  const [assignOrderId, setAssignOrderId] = useState(null);
  const [selectedDriverId, setSelectedDriverId] = useState('');
  
  // Dispatch State
  const [dispatchQueue, setDispatchQueue] = useState([]);
  const [dispatchStats, setDispatchStats] = useState(null);
  
  // Abbuchen Dialog
  const [showUnassignDialog, setShowUnassignDialog] = useState(false);
  const [unassignOrderId, setUnassignOrderId] = useState(null);

  const token = localStorage.getItem('access_token');

  // Load Orders
  const loadOrders = useCallback(async () => {
    try {
      setLoading(true);
      const data = await deliveryApi.getDeliveryOrders();
      
      const sorted = (data || []).sort((a, b) => {
        const priorityA = STATUS_CONFIG[a.status]?.priority || 99;
        const priorityB = STATUS_CONFIG[b.status]?.priority || 99;
        if (priorityA !== priorityB) return priorityA - priorityB;
        return new Date(a.created_at) - new Date(b.created_at);
      });
      
      setOrders(sorted);
    } catch (err) {
      console.error('Error loading orders:', err);
      setMessage({ type: 'error', text: err.message });
    } finally {
      setLoading(false);
    }
  }, []);

  // Load Drivers (from new dispatch API)
  const loadDrivers = useCallback(async () => {
    try {
      const response = await fetch(`${API_BASE}/api/dispatch/drivers`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setDrivers(data.drivers || []);
      }
    } catch (err) {
      console.error('Error loading drivers:', err);
    }
  }, [token]);

  // Load Dispatch Queue
  const loadDispatchQueue = useCallback(async () => {
    try {
      const response = await fetch(`${API_BASE}/api/dispatch/queue?status=ready`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setDispatchQueue(data.orders || []);
      }
    } catch (err) {
      console.error('Error loading dispatch queue:', err);
    }
  }, [token]);

  // Load Stats
  const loadStats = useCallback(async () => {
    try {
      const response = await fetch(`${API_BASE}/api/dispatch/stats`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setDispatchStats(data);
      }
    } catch (err) {
      console.error('Error loading stats:', err);
    }
  }, [token]);

  useEffect(() => {
    loadOrders();
    loadDrivers();
    loadDispatchQueue();
    loadStats();
    
    const interval = setInterval(() => {
      loadOrders();
      loadDrivers();
      loadDispatchQueue();
    }, 15000);
    
    return () => clearInterval(interval);
  }, [loadOrders, loadDrivers, loadDispatchQueue, loadStats]);

  // Assign Driver (using new dispatch API)
  const assignDriver = async () => {
    if (!assignOrderId || !selectedDriverId) return;
    
    try {
      const response = await fetch(`${API_BASE}/api/dispatch/assign`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          order_id: assignOrderId,
          driver_id: selectedDriverId
        })
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Zuweisung fehlgeschlagen');
      }
      
      const data = await response.json();
      toast.success(`Zugewiesen an ${data.driver_name}`);
      setShowAssignDialog(false);
      setAssignOrderId(null);
      setSelectedDriverId('');
      loadOrders();
      loadDispatchQueue();
      loadDrivers();
    } catch (err) {
      toast.error(err.message);
    }
  };

  // Unassign Order
  const unassignOrder = async () => {
    if (!unassignOrderId) return;
    
    try {
      const response = await fetch(`${API_BASE}/api/dispatch/unassign/${unassignOrderId}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Abbuchen fehlgeschlagen');
      }
      
      toast.success('Zuweisung entfernt');
      setShowUnassignDialog(false);
      setUnassignOrderId(null);
      loadOrders();
      loadDispatchQueue();
    } catch (err) {
      toast.error(err.message);
    }
  };

  // Check-in Driver
  const checkInDriver = async (driverId) => {
    try {
      const response = await fetch(`${API_BASE}/api/dispatch/driver/${driverId}/check-in`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        toast.success(data.message);
        loadDrivers();
      } else {
        const err = await response.json();
        toast.error(err.detail);
      }
    } catch (err) {
      toast.error('Verbindungsfehler');
    }
  };

  // Check-out Driver
  const checkOutDriver = async (driverId) => {
    try {
      const response = await fetch(`${API_BASE}/api/dispatch/driver/${driverId}/check-out`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        toast.success(`Ausgecheckt (${data.duration_minutes} Min)`);
        loadDrivers();
      } else {
        const err = await response.json();
        toast.error(err.detail);
      }
    } catch (err) {
      toast.error('Verbindungsfehler');
    }
  };

  const updateOrderStatus = async (orderId, newStatus) => {
    try {
      await deliveryApi.updateOrderStatus(orderId, {
        status: newStatus,
        actor: 'kassier'
      });
      
      toast.success(`Status: ${STATUS_CONFIG[newStatus].label}`);
      loadOrders();
      loadDispatchQueue();
    } catch (err) {
      toast.error('Fehler: ' + err.message);
    }
  };

  const getStatusBadge = (status) => {
    const config = STATUS_CONFIG[status] || STATUS_CONFIG.new;
    return (
      <span className={`px-3 py-1 rounded-full text-sm font-medium ${config.color}`}>
        {config.icon} {config.label}
      </span>
    );
  };

  const getNextActions = (order) => {
    const actions = [];
    
    if (order.status === 'new') {
      actions.push({ status: 'preparing', label: '👨‍🍳 In Zubereitung', color: 'bg-yellow-500' });
    }
    if (order.status === 'preparing') {
      actions.push({ status: 'ready', label: '✅ Fertig', color: 'bg-green-600' });
    }
    if (order.status === 'ready' && !order.driver_id) {
      actions.push({ status: 'dispatched', label: '🚗 Manuell dispatchen', color: 'bg-purple-600' });
    }
    if (order.status === 'dispatched') {
      actions.push({ status: 'delivered', label: '✓ Zugestellt', color: 'bg-gray-600' });
    }
    if (order.status !== 'cancelled' && order.status !== 'delivered') {
      actions.push({ status: 'cancelled', label: '✕ Abbrechen', color: 'bg-red-600' });
    }
    
    return actions;
  };

  const getUrgencyClass = (order) => {
    if (order.status === 'delivered' || order.status === 'cancelled') return '';
    
    const created = new Date(order.created_at);
    const now = new Date();
    const minutesOld = (now - created) / 1000 / 60;
    
    if (minutesOld > 30) return 'border-l-4 border-red-500 bg-red-50';
    if (minutesOld > 15) return 'border-l-4 border-yellow-500 bg-yellow-50';
    return '';
  };

  const getVehicleIcon = (vehicle) => {
    switch (vehicle) {
      case 'bike':
      case 'fahrrad':
        return <Bike className="w-4 h-4" />;
      default:
        return <Car className="w-4 h-4" />;
    }
  };

  // Count orders by status
  const readyCount = orders.filter(o => o.status === 'ready' && !o.driver_id).length;
  const dispatchedCount = orders.filter(o => o.status === 'dispatched').length;
  const activeDrivers = drivers.filter(d => d.checked_in).length;

  return (
    <div data-testid="lieferungen-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-3xl font-bold" style={{ fontFamily: 'Chivo, sans-serif' }}>
            Lieferungen
          </h1>
          <p className="text-gray-500 mt-1">
            Dispatch & Fahrerverwaltung
          </p>
        </div>
        <Button
          onClick={() => {
            loadOrders();
            loadDrivers();
            loadDispatchQueue();
            loadStats();
          }}
          variant="outline"
          className="gap-2"
          data-testid="refresh-btn"
        >
          <RefreshCw className="w-4 h-4" />
          Aktualisieren
        </Button>
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <Card className="bg-green-50 border-green-200">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-green-500/20 flex items-center justify-center">
                <Package className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-green-700">{readyCount}</p>
                <p className="text-xs text-green-600">Bereit zur Auslieferung</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-purple-50 border-purple-200">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-purple-500/20 flex items-center justify-center">
                <Truck className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-purple-700">{dispatchedCount}</p>
                <p className="text-xs text-purple-600">Unterwegs</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-blue-50 border-blue-200">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-blue-500/20 flex items-center justify-center">
                <Users className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-blue-700">{activeDrivers}</p>
                <p className="text-xs text-blue-600">Fahrer aktiv</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-amber-50 border-amber-200">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-amber-500/20 flex items-center justify-center">
                <TrendingUp className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-amber-700">
                  {dispatchStats?.summary?.total_delivered || 0}
                </p>
                <p className="text-xs text-amber-600">Heute geliefert</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <TabsList className="grid w-full grid-cols-4">
          <TabsTrigger value="orders" className="gap-2">
            <Package className="w-4 h-4" />
            Bestellungen
          </TabsTrigger>
          <TabsTrigger value="dispatch" className="gap-2">
            <Truck className="w-4 h-4" />
            Dispatch
            {readyCount > 0 && (
              <Badge variant="destructive" className="ml-1 h-5 w-5 p-0 flex items-center justify-center text-xs">
                {readyCount}
              </Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="drivers" className="gap-2">
            <Users className="w-4 h-4" />
            Fahrer
          </TabsTrigger>
          <TabsTrigger value="leaderboard" className="gap-2">
            <Trophy className="w-4 h-4" />
            Ranking
          </TabsTrigger>
        </TabsList>

        {/* Orders Tab */}
        <TabsContent value="orders" className="space-y-4">
          {loading && orders.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <RefreshCw className="w-8 h-8 animate-spin mx-auto mb-2" />
              Lade Lieferungen...
            </div>
          ) : orders.length === 0 ? (
            <Card className="p-12 text-center text-gray-500">
              <Package className="w-12 h-12 mx-auto mb-3 opacity-50" />
              Keine Lieferungen vorhanden
            </Card>
          ) : (
            <div className="grid gap-4" data-testid="delivery-orders-list">
              {orders.map((order) => (
                <Card
                  key={order.id}
                  className={`p-6 ${getUrgencyClass(order)} ${order.status === 'ready' ? 'border-2 border-green-500 shadow-lg' : ''}`}
                  data-testid={`order-${order.id}`}
                >
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <h3 className="text-lg font-bold">{order.customer_name}</h3>
                        {getStatusBadge(order.status)}
                      </div>
                      
                      <div className="text-sm text-gray-600 space-y-1">
                        <div className="flex items-center gap-2">
                          <Phone className="w-4 h-4" />
                          <span>{order.customer_phone}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <MapPin className="w-4 h-4" />
                          <span>{order.delivery_address?.formatted_address || order.delivery_address}</span>
                        </div>
                        {order.notes && (
                          <div className="flex items-center gap-2 text-amber-700 bg-amber-50 p-2 rounded mt-2">
                            <AlertCircle className="w-4 h-4" />
                            <span className="font-medium">{order.notes}</span>
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="text-right">
                      <div className="text-2xl font-bold text-green-600">
                        {formatCurrency(order.total_gross)}
                      </div>
                      <div className="text-xs text-gray-500 mt-1">
                        {formatDate(order.created_at)}
                      </div>
                      {order.driver_name && (
                        <div className="text-xs text-purple-600 mt-2 font-medium flex items-center justify-end gap-1">
                          <Truck className="w-3 h-3" />
                          {order.driver_name}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Items */}
                  <div className="mb-4 p-3 bg-gray-50 rounded-lg">
                    <div className="text-xs font-medium text-gray-500 mb-2">Artikel:</div>
                    <div className="space-y-1">
                      {order.items?.map((item, idx) => (
                        <div key={idx} className="text-sm flex justify-between">
                          <span>{item.qty}x {item.name}</span>
                          <span className="font-medium">{formatCurrency(item.qty * item.price_net * (1 + (item.vat_rate || 0) / 100))}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex flex-wrap gap-2">
                    {getNextActions(order).map((action, idx) => (
                      <Button
                        key={idx}
                        onClick={() => updateOrderStatus(order.id, action.status)}
                        className={`${action.color} text-white`}
                        size="sm"
                        data-testid={`action-${action.status}-${order.id}`}
                      >
                        {action.label}
                      </Button>
                    ))}
                    
                    {(order.status === 'ready' || order.status === 'new' || order.status === 'preparing') && !order.driver_id && (
                      <Button
                        onClick={() => {
                          setAssignOrderId(order.id);
                          setShowAssignDialog(true);
                        }}
                        className="bg-purple-600 hover:bg-purple-700 text-white"
                        size="sm"
                        data-testid={`assign-driver-${order.id}`}
                      >
                        <UserCheck className="w-4 h-4 mr-1" />
                        Fahrer zuweisen
                      </Button>
                    )}
                    
                    {order.driver_id && order.status === 'dispatched' && (
                      <Button
                        onClick={() => {
                          setUnassignOrderId(order.id);
                          setShowUnassignDialog(true);
                        }}
                        variant="outline"
                        className="text-orange-600 border-orange-300 hover:bg-orange-50"
                        size="sm"
                        data-testid={`unassign-${order.id}`}
                      >
                        <UserX className="w-4 h-4 mr-1" />
                        Abbuchen
                      </Button>
                    )}
                  </div>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        {/* Dispatch Tab */}
        <TabsContent value="dispatch" className="space-y-4">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Queue */}
            <div className="lg:col-span-2">
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="flex items-center gap-2 text-lg">
                    <Package className="w-5 h-5 text-green-600" />
                    Bereit zur Auslieferung
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {dispatchQueue.length === 0 ? (
                    <div className="text-center py-8 text-gray-400">
                      <CheckCircle className="w-12 h-12 mx-auto mb-2 opacity-50" />
                      <p>Alle Bestellungen zugewiesen!</p>
                    </div>
                  ) : (
                    <ScrollArea className="h-[400px]">
                      <div className="space-y-3">
                        {dispatchQueue.map((order) => (
                          <div
                            key={order.id || order._id}
                            className="p-4 bg-gray-50 rounded-lg border border-gray-200 hover:border-green-300 transition-colors"
                            data-testid="dispatch-queue-item"
                          >
                            <div className="flex items-start justify-between">
                              <div className="flex-1">
                                <div className="flex items-center gap-2 mb-2">
                                  <Badge variant="outline" className="text-green-600 border-green-300">
                                    #{order.order_number || order.transaction_id?.slice(-6)}
                                  </Badge>
                                  <span className="font-semibold">
                                    {(order.total_gross || order.total || 0).toFixed(2)}€
                                  </span>
                                </div>
                                <p className="text-sm font-medium">{order.customer_name || 'Kunde'}</p>
                                <p className="text-sm text-gray-500 flex items-center gap-1">
                                  <MapPin className="w-3 h-3" />
                                  {order.formatted_address || order.delivery_address}
                                </p>
                              </div>
                              <Button
                                size="sm"
                                onClick={() => {
                                  setAssignOrderId(order.id || order._id);
                                  setShowAssignDialog(true);
                                }}
                                className="bg-green-600 hover:bg-green-700"
                              >
                                Zuweisen
                                <ArrowRight className="w-4 h-4 ml-1" />
                              </Button>
                            </div>
                          </div>
                        ))}
                      </div>
                    </ScrollArea>
                  )}
                </CardContent>
              </Card>
            </div>

            {/* Active Drivers */}
            <div>
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="flex items-center gap-2 text-lg">
                    <Users className="w-5 h-5 text-blue-600" />
                    Aktive Fahrer
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {drivers.filter(d => d.checked_in).length === 0 ? (
                    <div className="text-center py-8 text-gray-400">
                      <Users className="w-10 h-10 mx-auto mb-2 opacity-50" />
                      <p>Kein Fahrer eingecheckt</p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {drivers.filter(d => d.checked_in).map((driver) => (
                        <div
                          key={driver.driver_id}
                          className="p-3 bg-green-50 rounded-lg border border-green-200"
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              {getVehicleIcon(driver.vehicle)}
                              <span className="font-medium">{driver.name}</span>
                            </div>
                            <Badge variant="outline" className="text-purple-600 border-purple-300">
                              {driver.active_orders || 0} aktiv
                            </Badge>
                          </div>
                          <p className="text-xs text-gray-500 mt-1">
                            {driver.delivered_today || 0} heute geliefert
                          </p>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Ranking */}
              {dispatchStats?.ranking && dispatchStats.ranking.length > 0 && (
                <Card className="mt-4">
                  <CardHeader className="pb-2">
                    <CardTitle className="flex items-center gap-2 text-sm">
                      <Trophy className="w-4 h-4 text-amber-500" />
                      Top Fahrer heute
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2">
                      {dispatchStats.ranking.slice(0, 3).map((driver, idx) => (
                        <div key={driver.driver_id} className="flex items-center gap-2">
                          <span className={`text-lg font-bold ${
                            idx === 0 ? 'text-amber-500' : 
                            idx === 1 ? 'text-gray-400' : 'text-amber-700'
                          }`}>
                            {idx + 1}.
                          </span>
                          <span className="flex-1">{driver.driver_name}</span>
                          <Badge variant="outline" className="text-green-600">
                            {driver.delivered} ✓
                          </Badge>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>
          </div>
        </TabsContent>

        {/* Drivers Tab */}
        <TabsContent value="drivers" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Users className="w-5 h-5" />
                Fahrer-Verwaltung
              </CardTitle>
            </CardHeader>
            <CardContent>
              {drivers.length === 0 ? (
                <div className="text-center py-12 text-gray-400">
                  <Users className="w-12 h-12 mx-auto mb-3 opacity-50" />
                  <p>Keine Fahrer vorhanden</p>
                  <p className="text-sm mt-1">Fahrer können unter "Team" hinzugefügt werden.</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {drivers.map((driver) => (
                    <Card
                      key={driver.driver_id}
                      className={`p-4 ${driver.checked_in ? 'bg-green-50 border-green-200' : 'bg-gray-50'}`}
                      data-testid={`driver-card-${driver.driver_id}`}
                    >
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-3">
                          <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                            driver.checked_in ? 'bg-green-500/20' : 'bg-gray-200'
                          }`}>
                            {getVehicleIcon(driver.vehicle)}
                          </div>
                          <div>
                            <p className="font-semibold">{driver.name}</p>
                            <p className="text-xs text-gray-500">
                              {driver.checked_in ? '● Aktiv' : '○ Nicht eingecheckt'}
                            </p>
                          </div>
                        </div>
                      </div>

                      {driver.checked_in && (
                        <div className="grid grid-cols-2 gap-2 text-sm mb-3">
                          <div className="bg-white rounded p-2 text-center">
                            <p className="text-lg font-bold text-purple-600">{driver.active_orders || 0}</p>
                            <p className="text-xs text-gray-500">Aktiv</p>
                          </div>
                          <div className="bg-white rounded p-2 text-center">
                            <p className="text-lg font-bold text-green-600">{driver.delivered_today || 0}</p>
                            <p className="text-xs text-gray-500">Geliefert</p>
                          </div>
                        </div>
                      )}

                      <Button
                        onClick={() => driver.checked_in 
                          ? checkOutDriver(driver.driver_id)
                          : checkInDriver(driver.driver_id)
                        }
                        className={`w-full ${driver.checked_in 
                          ? 'bg-red-500 hover:bg-red-600' 
                          : 'bg-green-600 hover:bg-green-700'
                        }`}
                        size="sm"
                        data-testid={driver.checked_in ? `checkout-${driver.driver_id}` : `checkin-${driver.driver_id}`}
                      >
                        {driver.checked_in ? (
                          <>
                            <UserX className="w-4 h-4 mr-2" />
                            Auschecken
                          </>
                        ) : (
                          <>
                            <UserCheck className="w-4 h-4 mr-2" />
                            Einchecken
                          </>
                        )}
                      </Button>
                    </Card>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Leaderboard Tab - Gamification */}
        <TabsContent value="leaderboard" className="space-y-4">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Main Leaderboard */}
            <div className="lg:col-span-2">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Trophy className="w-5 h-5 text-amber-500" />
                    Fahrer-Ranking heute
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {!dispatchStats?.ranking || dispatchStats.ranking.length === 0 ? (
                    <div className="text-center py-12 text-gray-400">
                      <Trophy className="w-12 h-12 mx-auto mb-3 opacity-50" />
                      <p>Noch keine Lieferungen heute</p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {dispatchStats.ranking.map((driver, idx) => (
                        <div
                          key={driver.driver_id}
                          className={`p-4 rounded-lg border-2 transition-all ${
                            idx === 0 ? 'bg-amber-50 border-amber-300 shadow-lg' :
                            idx === 1 ? 'bg-gray-100 border-gray-300' :
                            idx === 2 ? 'bg-orange-50 border-orange-200' :
                            'bg-white border-gray-200'
                          }`}
                        >
                          <div className="flex items-center gap-4">
                            {/* Rank */}
                            <div className={`w-12 h-12 rounded-full flex items-center justify-center text-xl font-bold ${
                              idx === 0 ? 'bg-amber-400 text-white' :
                              idx === 1 ? 'bg-gray-400 text-white' :
                              idx === 2 ? 'bg-orange-400 text-white' :
                              'bg-gray-200 text-gray-600'
                            }`}>
                              {idx === 0 ? '🥇' : idx === 1 ? '🥈' : idx === 2 ? '🥉' : driver.rank}
                            </div>
                            
                            {/* Driver Info */}
                            <div className="flex-1">
                              <div className="flex items-center gap-2">
                                <span className="font-bold text-lg">{driver.driver_name}</span>
                                <span className="text-lg">{driver.level_icon}</span>
                                <Badge variant="outline" className="text-xs">
                                  {driver.level_name}
                                </Badge>
                              </div>
                              <div className="flex items-center gap-4 mt-1 text-sm text-gray-500">
                                <span>{driver.delivered} Lieferungen</span>
                                <span>⌀ {driver.avg_delivery_time_minutes} Min</span>
                                {driver.total_tips > 0 && (
                                  <span className="text-green-600">+{driver.total_tips.toFixed(2)}€ TG</span>
                                )}
                              </div>
                              {/* Badges */}
                              {driver.badges && driver.badges.length > 0 && (
                                <div className="flex gap-1 mt-2">
                                  {driver.badges.map((badge, bIdx) => (
                                    <span 
                                      key={bIdx}
                                      className="px-2 py-0.5 bg-white rounded-full text-xs border shadow-sm"
                                      title={badge.name}
                                    >
                                      {badge.icon} {badge.name}
                                    </span>
                                  ))}
                                </div>
                              )}
                            </div>
                            
                            {/* Score */}
                            <div className="text-right">
                              <p className="text-3xl font-bold text-green-600">{driver.delivered}</p>
                              <p className="text-xs text-gray-500">Geliefert</p>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>

            {/* Side Stats */}
            <div className="space-y-4">
              {/* Top Performer */}
              {dispatchStats?.gamification?.top_performer && (
                <Card className="bg-gradient-to-br from-amber-50 to-amber-100 border-amber-200">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm flex items-center gap-2 text-amber-700">
                      👑 Top Performer
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-xl font-bold text-amber-800">
                      {dispatchStats.gamification.top_performer.driver_name}
                    </p>
                    <p className="text-sm text-amber-600">
                      {dispatchStats.gamification.top_performer.delivered} Lieferungen
                    </p>
                  </CardContent>
                </Card>
              )}

              {/* Fastest */}
              {dispatchStats?.gamification?.fastest_driver && (
                <Card className="bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm flex items-center gap-2 text-blue-700">
                      ⚡ Schnellster
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-xl font-bold text-blue-800">
                      {dispatchStats.gamification.fastest_driver.driver_name}
                    </p>
                    <p className="text-sm text-blue-600">
                      ⌀ {dispatchStats.gamification.fastest_driver.avg_delivery_time_minutes} Min
                    </p>
                  </CardContent>
                </Card>
              )}

              {/* Most Tips */}
              {dispatchStats?.gamification?.most_tips && dispatchStats.gamification.most_tips.total_tips > 0 && (
                <Card className="bg-gradient-to-br from-green-50 to-green-100 border-green-200">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm flex items-center gap-2 text-green-700">
                      💰 Meiste Trinkgelder
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-xl font-bold text-green-800">
                      {dispatchStats.gamification.most_tips.driver_name}
                    </p>
                    <p className="text-sm text-green-600">
                      {dispatchStats.gamification.most_tips.total_tips.toFixed(2)}€
                    </p>
                  </CardContent>
                </Card>
              )}

              {/* Total Stats */}
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm">Heute gesamt</CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  <div className="flex justify-between">
                    <span className="text-gray-500">Geliefert</span>
                    <span className="font-bold">{dispatchStats?.summary?.total_delivered || 0}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">Unterwegs</span>
                    <span className="font-bold">{dispatchStats?.summary?.total_in_progress || 0}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">Bar eingenommen</span>
                    <span className="font-bold">{(dispatchStats?.summary?.total_cash_collected || 0).toFixed(2)}€</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">Trinkgelder</span>
                    <span className="font-bold text-green-600">+{(dispatchStats?.summary?.total_tips || 0).toFixed(2)}€</span>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </TabsContent>
      </Tabs>
      
      {/* Assign Dialog */}
      <Dialog open={showAssignDialog} onOpenChange={setShowAssignDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UserCheck className="w-5 h-5 text-purple-600" />
              Fahrer zuweisen
            </DialogTitle>
            <DialogDescription>
              Wählen Sie einen eingecheckten Fahrer aus.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <Select value={selectedDriverId} onValueChange={setSelectedDriverId}>
              <SelectTrigger data-testid="driver-select">
                <SelectValue placeholder="Fahrer auswählen..." />
              </SelectTrigger>
              <SelectContent>
                {drivers.filter(d => d.checked_in).map((driver) => (
                  <SelectItem key={driver.driver_id} value={driver.driver_id}>
                    <div className="flex items-center gap-2">
                      {getVehicleIcon(driver.vehicle)}
                      <span>{driver.name}</span>
                      <Badge variant="outline" className="ml-2 text-xs">
                        {driver.active_orders || 0} aktiv
                      </Badge>
                      {driver.score === drivers.filter(d => d.checked_in)[0]?.score && (
                        <Star className="w-3 h-3 text-amber-500" />
                      )}
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            
            {drivers.filter(d => d.checked_in).length === 0 && (
              <p className="text-sm text-gray-500 text-center py-2">
                Kein Fahrer eingecheckt. Bitte zuerst Fahrer einchecken.
              </p>
            )}
            
            <div className="flex gap-2">
              <Button
                variant="outline"
                onClick={() => {
                  setShowAssignDialog(false);
                  setSelectedDriverId('');
                }}
                className="flex-1"
              >
                Abbrechen
              </Button>
              <Button
                onClick={assignDriver}
                disabled={!selectedDriverId}
                className="flex-1 bg-purple-600 hover:bg-purple-700"
                data-testid="confirm-assign-btn"
              >
                <CheckCircle className="w-4 h-4 mr-2" />
                Zuweisen
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
      
      {/* Unassign Dialog */}
      <Dialog open={showUnassignDialog} onOpenChange={setShowUnassignDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-orange-600">
              <UserX className="w-5 h-5" />
              Lieferung abbuchen
            </DialogTitle>
            <DialogDescription>
              Die Fahrer-Zuweisung wird entfernt und die Lieferung wieder freigegeben.
            </DialogDescription>
          </DialogHeader>
          
          <div className="flex gap-2 pt-4">
            <Button
              variant="outline"
              onClick={() => setShowUnassignDialog(false)}
              className="flex-1"
            >
              Abbrechen
            </Button>
            <Button
              onClick={unassignOrder}
              className="flex-1 bg-orange-600 hover:bg-orange-700"
              data-testid="confirm-unassign-btn"
            >
              Abbuchen
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default Lieferungen;
